import React, { useState, useEffect } from 'react'
import { getActiveCrew } from '../lib/crew'
import { BILLING_METHODS } from '../lib/constants'
import { guardModalScroll } from '../lib/modalScroll'

export const EMPTY_PROPERTY_FORM = {
  name: '',
  address: '',
  phone: '',
  billing_email: '',
  property_manager_name: '',
  property_manager_phone: '',
  property_manager_email: '',
  maintenance_supervisor_name: '',
  maintenance_supervisor_phone: '',
  management_company: '',
  unit_count: '',
  standing_directions: '',
  property_notes: '',
  do_not_service: false,
  past_due_invoice: false,
  assigned_crew_id: null,
  billing_method: 'None',
}

const TEXTAREA_CLASS =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y min-h-[6rem] max-h-64'

export function buildPropertyForm(data) {
  const form = { ...EMPTY_PROPERTY_FORM }
  if (!data) return form
  form.name = data.name ?? ''
  form.address = data.address ?? ''
  form.phone = data.phone ?? ''
  form.billing_email = data.billing_email ?? ''
  form.property_manager_name = data.property_manager_name ?? ''
  form.property_manager_phone = data.property_manager_phone ?? ''
  form.property_manager_email = data.property_manager_email ?? ''
  form.maintenance_supervisor_name = data.maintenance_supervisor_name ?? ''
  form.maintenance_supervisor_phone = data.maintenance_supervisor_phone ?? ''
  form.management_company = data.management_company ?? ''
  form.unit_count = data.unit_count ?? ''
  form.standing_directions = data.standing_directions ?? ''
  form.property_notes = data.property_notes ?? ''
  form.do_not_service = !!data.do_not_service
  form.past_due_invoice = !!data.past_due_invoice
  form.assigned_crew_id = data.assigned_crew_id || null
  form.billing_method = data.billing_method || 'None'
  if (data.id) form.id = data.id
  return form
}

export default function PropertyModal({ isOpen, onClose, onSave, initialData }) {
  const initialId = initialData?.id ?? null
  const [form, setForm] = useState(() => buildPropertyForm(initialData))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [crewMembers, setCrewMembers] = useState([])

  // Reset by property id (not object identity) so switching records cannot bleed fields.
  useEffect(() => {
    if (!isOpen) {
      setForm(buildPropertyForm(null))
      setError('')
      return
    }
    setForm(buildPropertyForm(initialData))
    setError('')
  }, [isOpen, initialId, initialData])

  useEffect(() => {
    if (!isOpen) return
    let isMounted = true
    getActiveCrew().then(data => { if (isMounted) setCrewMembers(data) })
    return () => { isMounted = false }
  }, [isOpen])

  const selectedCrew = crewMembers.find(c => c.id === form.assigned_crew_id)

  const handleSave = async () => {
    if (!form.name || !form.address) {
      setError('Property name and address are required')
      return
    }
    setSaving(true)
    setError('')
    try {
      await onSave(form)
      onClose()
    } catch (err) {
      setError(err.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  const field = (label, key, type = 'text', required = false) => (
    <div key={key}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        value={form[key]}
        onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
      />
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div
        data-modal-scroll
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto [overflow-anchor:none]"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            {initialData?.id ? 'Edit Property' : 'Add Property'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}

          {field('Property Name', 'name', 'text', true)}
          {field('Address', 'address', 'text', true)}

          <div className="grid grid-cols-2 gap-3">
            {field('Phone', 'phone', 'tel')}
            {field('Billing Email', 'billing_email', 'email')}
          </div>

          <div className="grid grid-cols-3 gap-3">
            {field('Property Manager', 'property_manager_name')}
            {field('Manager Phone', 'property_manager_phone', 'tel')}
            {field('Manager Email', 'property_manager_email', 'email')}
          </div>

          <div className="grid grid-cols-2 gap-3">
            {field('Maintenance Supervisor', 'maintenance_supervisor_name')}
            {field('Supervisor Phone', 'maintenance_supervisor_phone', 'tel')}
          </div>

          {field('Management Company', 'management_company')}

          <div className="grid grid-cols-2 gap-3">
            {field('Unit Count', 'unit_count', 'number')}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Billing Method</label>
              <select
                value={form.billing_method || 'None'}
                onChange={e => setForm(prev => ({ ...prev, billing_method: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
              >
                {BILLING_METHODS.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Standing Directions</label>
            <textarea
              value={form.standing_directions}
              onChange={e => setForm(prev => ({ ...prev, standing_directions: e.target.value }))}
              onMouseDown={guardModalScroll}
              onSelect={guardModalScroll}
              rows={5}
              placeholder="Standing instructions that print on every job..."
              className={TEXTAREA_CLASS}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Property Notes</label>
            <textarea
              value={form.property_notes}
              onChange={e => setForm(prev => ({ ...prev, property_notes: e.target.value }))}
              onMouseDown={guardModalScroll}
              onSelect={guardModalScroll}
              rows={5}
              placeholder="Internal notes about this property (not printed)..."
              className={TEXTAREA_CLASS}
            />
          </div>

          {/* Assigned Tech */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Tech</label>
            <div className="flex items-center gap-2">
              {selectedCrew?.color && (
                <span
                  className="inline-block w-4 h-4 rounded-full border border-gray-300 flex-shrink-0"
                  style={{ backgroundColor: selectedCrew.color }}
                />
              )}
              <select
                value={form.assigned_crew_id || ''}
                onChange={e => setForm(prev => ({ ...prev, assigned_crew_id: e.target.value || null }))}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
              >
                <option value="">— None (no default tech) —</option>
                {crewMembers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <p className="mt-1 text-xs text-gray-500">When set, jobs for this property will show this tech's color dot on the calendar.</p>
          </div>

          {/* Do Not Service */}
          <div className="border border-red-200 bg-red-50 rounded-lg px-4 py-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.do_not_service}
                onChange={e => setForm(prev => ({ ...prev, do_not_service: e.target.checked }))}
                className="w-4 h-4 rounded border-red-400 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm font-semibold text-red-700"> Do Not Service</span>
            </label>
            <p className="mt-1 text-xs text-red-500 ml-7">Property will be highlighted in red across all menus and the calendar.</p>
          </div>

          {/* Past Due Invoice */}
          <div className="border border-orange-200 bg-orange-50 rounded-lg px-4 py-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.past_due_invoice}
                onChange={e => setForm(prev => ({ ...prev, past_due_invoice: e.target.checked }))}
                className="w-4 h-4 rounded border-orange-400 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm font-semibold text-orange-700">⚠️ Past Due Invoice</span>
            </label>
            <p className="mt-1 text-xs text-orange-500 ml-7">Property will be highlighted in orange across all menus and the calendar.</p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Property'}
          </button>
        </div>
      </div>
    </div>
  )
}
